import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentsIcon from "@mui/icons-material/Payments";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { formatDate } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import type { IPagamentoLoja, MetodoPagamento } from "interfaces/loja";
import {
  chipColorStatusPagamento,
  labelMetodoPagamentoLoja,
  labelStatusPagamentoLoja,
  resumoIdPagamento,
  textoParcelasCartao,
} from "utils/pagamentoLojaUi";
import {
  calcularResumoValoresPagamento,
  textoParcelasDetalhado,
} from "utils/pedidoValoresUi";
import { useNavigate } from "react-router-dom";

type PedidoPagamentosDetalheProps = {
  pagamentos: IPagamentoLoja[];
  valorPedido?: number | string;
  payLoading: boolean;
  saving?: boolean;
  onMarcarPago: (pagamentoId: string, metodo: MetodoPagamento) => void;
  onEstornar: (pagamentoId: string) => void;
  onCancelar: (pagamentoId: string) => void;
};

function iconeMetodo(metodo: string) {
  switch (metodo) {
    case "CARTAO_CREDITO":
    case "CARTAO_DEBITO":
      return <CreditCardIcon color="primary" fontSize="small" />;
    case "PIX":
      return <QrCode2Icon color="primary" fontSize="small" />;
    case "BOLETO":
      return <ReceiptLongIcon color="primary" fontSize="small" />;
    default:
      return <PaymentsIcon color="primary" fontSize="small" />;
  }
}

function LinhaInfo({
  rotulo,
  valor,
  monospace,
}: {
  rotulo: string;
  valor: ReactNode;
  monospace?: boolean;
}) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        fontWeight={600}
      >
        {rotulo}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={
          monospace
            ? { fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }
            : undefined
        }
      >
        {valor}
      </Typography>
    </Grid>
  );
}

export default function PedidoPagamentosDetalhe({
  pagamentos,
  valorPedido = 0,
  payLoading,
  saving = false,
  onMarcarPago,
  onEstornar,
  onCancelar,
}: PedidoPagamentosDetalheProps) {
  const navigate = useNavigate();

  return (
    <MDBox mb={3} id="sec-pagamento">
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <PaymentsIcon color="primary" />
        <Typography variant="button" fontWeight={700} color="primary">
          Pagamentos
        </Typography>
      </Stack>

      {payLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : pagamentos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum pagamento para este pedido.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {pagamentos.map((pg) => {
            const disablePagar =
              payLoading ||
              ["PAGO", "CANCELADO", "ESTORNADO"].includes(pg.status);
            const disableEstornar =
              payLoading || ["ESTORNADO", "CANCELADO"].includes(pg.status);
            const disableCancelar =
              payLoading || ["CANCELADO", "ESTORNADO"].includes(pg.status);
            const valorNum = Number(pg.valor);
            const resumoPg = calcularResumoValoresPagamento(valorPedido, pg);
            const parcelasTxt =
              textoParcelasDetalhado(pg.metodo, resumoPg) ??
              textoParcelasCartao(pg.metodo, pg.parcelas ?? null);
            const idPag = resumoIdPagamento(pg.id);
            const isCartao =
              pg.metodo === "CARTAO_CREDITO" || pg.metodo === "CARTAO_DEBITO";
            const rotuloValor = isCartao ? "Valor cobrado no cartão" : "Valor";

            return (
              <Card
                key={pg.id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "visible",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                }}
              >
                <MDBox p={2.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "flex-start" }}
                    justifyContent="space-between"
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      flex={1}
                      minWidth={0}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? "rgba(144,202,249,0.12)"
                              : "rgba(25,118,210,0.08)",
                          flexShrink: 0,
                        }}
                      >
                        {iconeMetodo(pg.metodo)}
                      </Box>
                      <Box minWidth={0} flex={1}>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          display="block"
                        >
                          {labelMetodoPagamentoLoja(pg.metodo)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {rotuloValor}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {formatCurrency(
                            Number.isFinite(valorNum) ? valorNum : 0
                          )}
                        </Typography>
                        {pg.moeda ? (
                          <Typography variant="caption" color="text.secondary">
                            Moeda: {pg.moeda}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                    <Chip
                      size="medium"
                      label={labelStatusPagamentoLoja(pg.status)}
                      color={chipColorStatusPagamento(pg.status)}
                      variant={pg.status === "PAGO" ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 700,
                        padding: 2,
                        alignSelf: { xs: "flex-start", sm: "center" },
                      }}
                    />
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    {parcelasTxt ? (
                      <LinhaInfo rotulo="Parcelamento" valor={parcelasTxt} />
                    ) : null}
                    {resumoPg.temJuros ? (
                      <>
                        <LinhaInfo
                          rotulo="Juros de parcelamento"
                          valor={formatCurrency(resumoPg.valorJuros)}
                        />
                        <LinhaInfo
                          rotulo="Valor do pedido (referência)"
                          valor={formatCurrency(resumoPg.valorPedido)}
                        />
                      </>
                    ) : null}
                    {pg.cartaoFinal?.trim() || pg.cardMask?.trim() ? (
                      <LinhaInfo
                        rotulo="Cartão"
                        valor={(pg.cartaoFinal ?? pg.cardMask ?? "").trim()}
                        monospace
                      />
                    ) : null}
                    {pg.chargeId?.trim() ? (
                      <LinhaInfo
                        rotulo="ID da cobrança (provedor)"
                        valor={pg.chargeId.trim()}
                        monospace
                      />
                    ) : null}
                    {pg.txid?.trim() ? (
                      <LinhaInfo
                        rotulo="TXID (PIX)"
                        valor={pg.txid.trim()}
                        monospace
                      />
                    ) : null}
                    {pg.bandeiraCartao?.trim() ? (
                      <LinhaInfo
                        rotulo="Bandeira"
                        valor={pg.bandeiraCartao.trim()}
                      />
                    ) : null}
                    {pg.provedor?.trim() ? (
                      <LinhaInfo rotulo="Provedor" valor={pg.provedor.trim()} />
                    ) : null}
                    <LinhaInfo
                      rotulo="ID do pagamento"
                      valor={<span title={idPag.completo}>{idPag.curto}</span>}
                      monospace
                    />
                    {pg.criadoEm || pg.createAt ? (
                      <LinhaInfo
                        rotulo="Registrado em"
                        valor={formatDate(
                          String(pg.criadoEm ?? pg.createAt),
                          "dd/MM/yyyy HH:mm:ss"
                        )}
                      />
                    ) : null}
                    {pg.autorizadoEm ? (
                      <LinhaInfo
                        rotulo="Autorizado em"
                        valor={formatDate(
                          pg.autorizadoEm,
                          "dd/MM/yyyy HH:mm:ss"
                        )}
                      />
                    ) : null}
                    {pg.pagoEm ? (
                      <LinhaInfo
                        rotulo="Pago em"
                        valor={formatDate(pg.pagoEm, "dd/MM/yyyy HH:mm:ss")}
                      />
                    ) : null}
                    {pg.canceladoEm ? (
                      <LinhaInfo
                        rotulo="Cancelado em"
                        valor={formatDate(
                          pg.canceladoEm,
                          "dd/MM/yyyy HH:mm:ss"
                        )}
                      />
                    ) : null}
                    {pg.estornadoEm ? (
                      <LinhaInfo
                        rotulo="Estornado em"
                        valor={formatDate(
                          pg.estornadoEm,
                          "dd/MM/yyyy HH:mm:ss"
                        )}
                      />
                    ) : null}
                  </Grid>

                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    justifyContent="flex-end"
                    sx={{ mt: 2.5, pt: 0 }}
                  >
                    <MDButton
                      size="small"
                      variant="gradient"
                      color="dark"
                      disabled={disablePagar || saving}
                      onClick={() => onMarcarPago(pg.id, pg.metodo)}
                    >
                      Marcar pago
                    </MDButton>

                    <MDButton
                      size="small"
                      variant="outlined"
                      color="dark"
                      onClick={() => navigate(`/loja/pagamentos/${pg.id}`)}
                    >
                      Ver pagamento
                    </MDButton>
                  </Stack>
                </MDBox>
              </Card>
            );
          })}
        </Stack>
      )}
    </MDBox>
  );
}
